/**
 * Microsoft Authentication Library (MSAL) Configuration
 * Azure AD Single Sign-On configuration for Darling's Forecast Dashboard
 */

import { Configuration, LogLevel } from '@azure/msal-browser';

// Azure AD Application Configuration
const AZURE_CLIENT_ID = '14c43073-f07f-4ed2-8cff-0265745cc2c4';
const AZURE_TENANT_ID = '0c92f65f-782b-462f-987e-bfcba4656cb2';

// Azure AD Group IDs for Role-Based Access Control
// Role hierarchy: Admin > CEO > Executive > GeneralManager > InsuranceManager* > MarketingManager* > DepartmentManager > Insurance > Unassigned
// *Insurance Manager and Marketing Manager override DepartmentManager (checked before DeptMgr)
// Regular Insurance agents do NOT override DepartmentManager
export const AZURE_AD_GROUPS = {
  // Admin: Full access including data refresh from DealerBuilt
  ADMINS: [
    '44da8ce4-058d-450c-9175-d1277f5694a4',
    '327aa422-51e9-48bf-8ba5-9ae36399aa65',
    '546da5af-13bd-437d-a19c-35bc6b4af426', // Accounting Managers
  ],
  // CEO: Executive access + AI Chat
  CEO: [
    'a364ea4d-940e-44ab-aff0-45277aec5cad',
  ],
  // Executive: Everything except data refresh
  EXECUTIVES: [
    'ebf208a7-94b0-471e-85ab-c119b1e295fd',
    'f26a80a0-599e-4335-8a49-1dcbbad7a136',
  ],
  // General Manager: No unlock, view-only config
  GENERAL_MANAGERS: [
    '60f1a173-d72d-428e-be9c-a04572449189',
  ],
  // Department Manager: Assigned location(s) + department(s), view-only projections
  DEPARTMENT_MANAGERS: [
    '0786b804-d7f2-4a74-87aa-e2d480c54cf0',
    'f678e61a-9f41-426e-8cf7-1a3a2ed1036e', // Rental Managers
  ],
  // Insurance Manager: Overrides DepartmentManager — checked BEFORE DeptMgr in role hierarchy
  INSURANCE_MANAGERS: [
    'a52b776e-4022-4e3b-a1e4-ab895772c539',
  ],
  // Insurance Agents: Does NOT override DepartmentManager — checked AFTER DeptMgr
  INSURANCE_AGENTS: [
    '1e2f83ec-04da-4c92-a096-631c0d1fb560',
    'a52b776e-4022-4e3b-a1e4-ab895772c539', // Insurance Managers (also caught here for non-DeptMgr members)
    'd3261010-11fa-46f3-9563-1ad735430d3a',
  ],
  // Marketing Manager: Reports page only (Marketing tab)
  MARKETING_MANAGERS: [
    'c7bc0c9a-4d03-4dbc-8747-49d3682ed074',
  ],
  // Accounting: Nut Sheet page ONLY (all locations, read-write), no other dashboard access.
  // Distinct from "Accounting Managers" (546da5af-… → Admin).
  ACCOUNTING: [
    'f378c494-34ac-4755-aecd-ba2d48b0ed1a',
    'be14d81f-d064-4243-89cb-24bad6ce001f',
    '57148d45-fcc6-49f1-b708-b3a1fae3aa81',
    'c5f9da88-cb72-4996-ab46-d02d3ba72b83',
    'b970a916-5b21-4257-ac16-6cddd541be4f',
    '27824044-1c4d-4668-9408-87a362b8ea63',
    '51e3643a-cbdc-480e-872e-e8e8bac550e8',
    '63cfca6b-7be2-49fc-b4f4-de6c5519c45b',
    '6842f63b-7b42-4730-b140-e8cc29ebf081',
  ],
  // Finance (F&I) Manager: Nut Sheet page ONLY, scoped to assigned location(s) (like
  // DepartmentManager) — no department gate. Distinct dedicated group.
  FINANCE_MANAGERS: [
    '5359f15a-8c71-4099-ae84-ec6a2c2f8688',
  ],
  // Team Service Manager: Forecast-only, SERVICE dept, auto-assigned location.
  // Role is group-only; store service groups are nested under this group and Entra emits transitive membership.
  TEAM_SERVICE_MANAGERS: [
    '5e395f3b-2056-46b4-a0c3-df5887b7c2bf',
  ],
} as const;

// Map group IDs to role names
export const GROUP_TO_ROLE: Record<string, UserRole> = {
  ...Object.fromEntries(AZURE_AD_GROUPS.ADMINS.map(id => [id, 'Admin' as UserRole])),
  ...Object.fromEntries(AZURE_AD_GROUPS.CEO.map(id => [id, 'CEO' as UserRole])),
  ...Object.fromEntries(AZURE_AD_GROUPS.EXECUTIVES.map(id => [id, 'Executive' as UserRole])),
  ...Object.fromEntries(AZURE_AD_GROUPS.GENERAL_MANAGERS.map(id => [id, 'GeneralManager' as UserRole])),
  ...Object.fromEntries(AZURE_AD_GROUPS.DEPARTMENT_MANAGERS.map(id => [id, 'DepartmentManager' as UserRole])),
  ...Object.fromEntries(AZURE_AD_GROUPS.INSURANCE_MANAGERS.map(id => [id, 'Insurance' as UserRole])),
  ...Object.fromEntries(AZURE_AD_GROUPS.INSURANCE_AGENTS.map(id => [id, 'Insurance' as UserRole])),
  ...Object.fromEntries(AZURE_AD_GROUPS.MARKETING_MANAGERS.map(id => [id, 'MarketingManager' as UserRole])),
  ...Object.fromEntries(AZURE_AD_GROUPS.ACCOUNTING.map(id => [id, 'Accounting' as UserRole])),
  ...Object.fromEntries(AZURE_AD_GROUPS.FINANCE_MANAGERS.map(id => [id, 'FinanceManager' as UserRole])),
  ...Object.fromEntries(AZURE_AD_GROUPS.TEAM_SERVICE_MANAGERS.map(id => [id, 'TeamServiceManager' as UserRole])),
};

// User roles in order of privilege (highest first)
// Note: Insurance is outside the main hierarchy - it's a separate access path
export type UserRole = 'Admin' | 'CEO' | 'Executive' | 'GeneralManager' | 'DepartmentManager' | 'TeamServiceManager' | 'Insurance' | 'MarketingManager' | 'Accounting' | 'FinanceManager' | 'Unassigned';

// Role hierarchy for permission checks (lower index = higher privilege)
// Note: Insurance is NOT in this hierarchy - it has its own separate access path
// Note: TeamServiceManager is NOT in this hierarchy - it has Forecast-only access
export const ROLE_HIERARCHY: UserRole[] = [
  'Admin',
  'CEO',
  'Executive',
  'GeneralManager',
  'DepartmentManager',
  'Unassigned',
];

export const TSM_STORE_GROUPS: Record<string, { locationId: number; name: string }> = {
  'e8ba3ec7-5f6a-4856-b45f-515929517ab1': { locationId: 1, name: 'Ford Service' },
  'e0c4bb06-613a-4ae5-9781-33011881cdaf': { locationId: 2, name: 'Honda Service' },
  '365d3eea-4991-4b5a-8093-44a7b3bb226a': { locationId: 3, name: 'Automall Service' },
  '29c3bdf9-42ff-4d88-8b69-668e44aba89f': { locationId: 4, name: 'Augusta Service' },
  '1e26c729-15f6-47d7-8b84-e31cc4b97b42': { locationId: 5, name: 'Chevy Service' },
  '5acf43e3-5b19-46fe-9c3e-91886901f1cf': { locationId: 10, name: 'Brunswick Service' },
  'c338e66e-4f81-4c57-91ef-5bdedba96db8': { locationId: 11, name: 'Newport Service' },
  'f7887b09-bc39-424a-81ef-95795c206b40': { locationId: 12, name: 'GreenpointServiceUsers' },
  '0aa6f373-ccd4-4235-a62e-48899c3e3782': { locationId: 14, name: 'KiaServiceUsers' },
  'ec86211f-272f-4727-8b69-63ede81b87f6': { locationId: 44, name: 'DowneastServiceUsers' },
};

export function resolveTsmLocationsFromGroups(groupIds: string[]): number[] {
  return Array.from(new Set(
    groupIds
      .filter(group => TSM_STORE_GROUPS[group])
      .map(group => TSM_STORE_GROUPS[group].locationId)
  )).sort((a, b) => a - b);
}

export const TSM_BODY_SHOP_LOCATIONS = [12, 44];

export function tsmDepartmentForLocation(locationId: number): 'SERVICE' | 'BODY_SHOP' {
  return TSM_BODY_SHOP_LOCATIONS.includes(locationId) ? 'BODY_SHOP' : 'SERVICE';
}

// Determine redirect URI based on environment
const getRedirectUri = (): string => {
  if (typeof window !== 'undefined') {
    // In browser - use current origin
    return window.location.origin;
  }
  // Fallback for SSR or build time
  return process.env.REACT_APP_REDIRECT_URI || 'http://localhost:3000';
};

/**
 * MSAL Configuration
 * @see https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-js-initializing-client-applications
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: AZURE_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${AZURE_TENANT_ID}`,
    redirectUri: getRedirectUri(),
    postLogoutRedirectUri: getRedirectUri(),
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage', // More secure than localStorage
    storeAuthStateInCookie: false,   // Set to true for IE11 support
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return; // Don't log PII
        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL]', message);
            break;
          case LogLevel.Warning:
            console.warn('[MSAL]', message);
            break;
          case LogLevel.Info:
            // Only log info in development
            if (process.env.NODE_ENV === 'development') {
              console.info('[MSAL]', message);
            }
            break;
          case LogLevel.Verbose:
            // Only log verbose in development with debug flag
            if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MSAL) {
              console.debug('[MSAL]', message);
            }
            break;
        }
      },
      logLevel: process.env.NODE_ENV === 'development' ? LogLevel.Warning : LogLevel.Error,
    },
  },
};

/**
 * Scopes for authentication
 * Empty scopes array = just authenticate the user (ID token only)
 * No additional permissions requested = no consent prompt needed
 *
 * User profile info (name, email) comes from the ID token claims.
 * Groups are read from token claims (configured in Azure AD App Registration).
 */
export const loginRequest = {
  scopes: [],
};
